<script setup lang="ts">
import type { DialogRootProps } from 'reka-ui'
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  useForwardPropsEmits,
} from 'reka-ui'
import { faXmark } from '@fortawesome/duotone-regular-svg-icons'
import TeleportationTarget from '~/models/TeleportationTarget'
import Button from '~/components/ui/Button.vue'

const props = withDefaults(defineProps<DialogRootProps & {
  title: string
  description?: string
  centerTitle?: boolean
  backdrop?: boolean
}>(), {
  backdrop: true,
})

const emits = defineEmits<{
  (e: 'close'): void
  (e: 'update:open', value: boolean): void
}>()

const handleUpdateOpen = (value: boolean) => !value && emits('close')

const forwarded = useForwardPropsEmits(props, emits)
</script>

<template>
  <DialogRoot
    v-bind="forwarded"
    @update:open="handleUpdateOpen"
  >
    <DialogTrigger as-child>
      <slot name="trigger" />
    </DialogTrigger>
    <DialogPortal :to="TeleportationTarget.AppTop">
      <Transition name="overlay">
        <DialogOverlay v-if="backdrop" class="fixed inset-0 z-40 bg-black/60" />
      </Transition>
      <Transition name="slide">
        <DialogContent class="text-green-5 fixed top-0 right-0 z-50 -mr-2 h-svh max-h-svh min-h-svh w-full max-w-[600px] overflow-y-auto border-r-8 border-green-50 bg-green-50 p-6  shadow-2xl">
          <DialogTitle class="mb-6 text-3xl font-bold" :class="{ 'text-center': centerTitle }">
            {{ title }}
          </DialogTitle>
          <DialogDescription v-if="description">
            {{ description }}
          </DialogDescription>
          <slot name="content" />
          <DialogClose as-child>
            <Button
              shape="square"
              size="xl"
              class="absolute top-6 right-6"
            >
              <font-awesome-icon :icon="faXmark" />
            </Button>
          </DialogClose>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<style scoped>
.slide-enter-active {
  animation: slide-in 0.4s;
}
.slide-leave-active {
  animation: slide-in 0.4s reverse;
}
.overlay-enter-active {
  animation: overlay-in 0.3s;
}
.overlay-leave-active {
  animation: overlay-in 0.3s reverse;
}
@keyframes slide-in {
  0% {
    transform: translateX(100%);
  }
  50% {
    transform: translateX(-10px);
  }
  100% {
    transform: translateX(0);
  }
}
@keyframes overlay-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
